import clsx from "clsx";
import {
  DOCS_FORM_NAMES,
  QUERY_PROGRESS_SUCCEED,
  RECARE_FILE_TYPE,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { DocsFormName, RecareFileType } from "core/types";
import { Button, Tooltip } from "ds/ui";
import { useCopy } from "dsl/atoms/CopyButton/useCopy";
import { CheckIcon, CopyIcon, FileSearch2Icon } from "lucide-react";
import { ReactNode } from "react";
import { useWatch } from "react-hook-form";
import { useTranslations } from "translations";
import { DocsFormDataMapped } from "./Forms";
import { SourceInformation } from "./useDocsForm";

export const FORM_NAME_TO_FILE_TYPE: Record<DocsFormName, RecareFileType> = {
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY]:
    RECARE_FILE_TYPE.DISCHARGE_LETTER_CARDIOLOGY,
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT]: RECARE_FILE_TYPE.DISCHARGE_LETTER_ENT,
  [DOCS_FORM_NAMES.DRV_GENERAL]: RECARE_FILE_TYPE.DRV_GENERAL_FORM,
  [DOCS_FORM_NAMES.DRV_MEDICAL]: RECARE_FILE_TYPE.DRV_MEDICAL_FORM,
  [DOCS_FORM_NAMES.GKV_GENERAL]: RECARE_FILE_TYPE.GKV_GENERAL_FORM,
  [DOCS_FORM_NAMES.GKV_MEDICAL]: RECARE_FILE_TYPE.GKV_MEDICAL_FORM,
  [DOCS_FORM_NAMES.TRANSITIONAL_CARE]: RECARE_FILE_TYPE.TRANSITIONAL_CARE,
};

export type MigratedForms =
  | typeof DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT
  | typeof DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY;

const CopyButton = <T extends DocsFormName = DocsFormName>({
  fieldName,
}: {
  fieldName: keyof DocsFormDataMapped<T>;
}) => {
  const fieldValue = useWatch({
    name: fieldName as string,
  });
  const translations = useTranslations();
  const { copyMessage, copyStatus, copyToClipboard } = useCopy({
    onCopyMessage: "todo: translation",
  });

  const renderContent = () => (
    <Button
      aria-label={translations.chat.copyMessage}
      color="primary"
      id={`copy-button-${String(fieldName)}`}
      isDisabled={!fieldValue}
      isIconOnly
      startContent={
        copyStatus === QUERY_PROGRESS_SUCCEED ? <CheckIcon /> : <CopyIcon />
      }
      trackEventProps={{
        field_name: String(fieldName),
        id: `copy-button`,
      }}
      variant="ghost"
      onPress={() => copyToClipboard(fieldValue)}
    />
  );

  if (copyStatus === QUERY_PROGRESS_SUCCEED) {
    return (
      <Tooltip content={copyMessage} isOpen placement="bottom">
        {renderContent()}
      </Tooltip>
    );
  }

  return renderContent();
};

export const SourceWrapper = <T extends DocsFormName = DocsFormName>({
  children,
  fieldName,
  formName,
  hideSource,
  isIconOnly,
  sourceInformation,
}: {
  children: ReactNode;
  sourceInformation: SourceInformation<T>;
  fieldName: keyof DocsFormDataMapped<T>;
  isIconOnly?: boolean;
  hideSource?: boolean;
  formName: T;
}) => {
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const translations = useTranslations();

  if (!children) {
    return null;
  }

  if ((!isDocsAndExtractActive && !isDischargeLetterActive) || hideSource) {
    return <div className="p-4">{children}</div>;
  }

  const isSelected = sourceInformation.isSourceSelected(fieldName);
  const fieldMetadata = sourceInformation.getFieldMetadata(fieldName);
  const isEdited = fieldMetadata.manually_edited;
  const sourceDocuments = fieldMetadata.source_documents ?? [];
  const canCopyResult = (
    [
      DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
      DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
    ] as DocsFormName[]
  ).includes(formName);

  return (
    <div
      className={clsx("flex w-full gap-2 rounded-md p-4", {
        "bg-blue-50": isSelected,
        "bg-white": !isSelected,
      })}
    >
      <div
        className={clsx("min-w-0", {
          "basis-[70%]": !isIconOnly,
          "basis-[85%]": isIconOnly,
        })}
      >
        {children}
      </div>
      <div
        className={clsx(
          "min-w-0 flex-1",
          "flex flex-row flex-wrap items-end gap-2",
          {
            "justify-between": isDischargeLetterActive,
            "justify-end": !isDischargeLetterActive,
          },
        )}
      >
        {canCopyResult && <CopyButton fieldName={fieldName} />}
        {sourceDocuments.length > 0 && (
          <Button
            aria-label={
              translations.extractDocs.splitView.sourceInformation.buttonSource
            }
            className="whitespace-nowrap"
            color="primary"
            endContent={<FileSearch2Icon />}
            id={`${isEdited ? "reference" : "source"}-button-${String(
              fieldName,
            )}`}
            isIconOnly={isIconOnly}
            trackEventProps={{
              field_name: String(fieldName),
              id: `${isEdited ? "reference" : "source"}-button`,
            }}
            variant={isEdited ? "light" : "ghost"}
            onPress={() => sourceInformation.handleSetSelectedSource(fieldName)}
          >
            {isIconOnly
              ? null
              : isEdited
              ? translations.extractDocs.splitView.sourceInformation
                  .buttonReference
              : translations.extractDocs.splitView.sourceInformation
                  .buttonSource}
          </Button>
        )}
      </div>
    </div>
  );
};
